<template>
    <div>
        <div class="flex justify-between items-center mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">Home</h2>
        </div>

        <div class="border border-gray-200 rounded-lg p-6 mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-4">Select Books for Slider</h3>

            <div class="mb-6">
                <div class="flex items-center border-b-2 border-gray-300 pb-0">
                    <input v-model="searchQuery" @input="handleSearch" type="text"
                        placeholder="Search books by title, author, or ISBN..."
                        class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-base font-body text-body leading-[1.75] mr-4" />
                    <div class="flex items-center">
                        <button v-if="searchQuery.trim()" type="button" @click="clearSearch"
                            class="p-2 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0"
                            aria-label="Clear search">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <button type="button" @click="handleSearch"
                            class="p-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex-shrink-0"
                            aria-label="Search">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="text-center py-8">
                <Loader size="medium" />
            </div>

            <div v-else-if="paginatedBooks.length === 0"
                class="text-gray-500 text-center py-8 font-body text-body leading-[1.75]">
                {{ searchQuery ? 'No books found matching your search.' : 'No books available. Please add books first.'
                }}
            </div>

            <div v-else class="space-y-4">
                <div class="space-y-3 mb-6">
                    <div v-for="book in paginatedBooks" :key="book.id" @click="toggleBookSelection(book.id)" :class="[
                        'p-4 border-2 rounded-lg cursor-pointer transition-all',
                        isBookSelected(book.id)
                            ? 'border-green-700 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                    ]">
                        <div class="flex items-center gap-4">
                            <div class="flex-shrink-0">
                                <img v-if="book.cover_image" :src="book.cover_image" :alt="book.title"
                                    class="w-16 h-20 object-cover rounded" />
                                <div v-else class="w-16 h-20 bg-gray-200 rounded flex items-center justify-center">
                                    <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                            </div>
                            <div class="flex-1 min-w-0">
                                <h4 class="font-header text-[1.5rem] font-bold text-primary">{{ book.title }}</h4>
                                <p class="text-sm text-gray-600 font-body text-body leading-[1.75]">{{ book.author }}
                                </p>
                            </div>
                            <div v-if="isBookSelected(book.id)" class="flex-shrink-0 flex items-center gap-3">
                                <span class="text-sm text-green-700 font-body font-bold">Selected</span>
                                <svg class="w-6 h-6 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="pagination.total > 0" class="flex justify-center items-center gap-2 mt-6">
                    <button @click="goToPage(pagination.page - 1)" :disabled="pagination.page === 1" :class="[
                        'px-4 py-2 rounded-md font-marketing transition-colors',
                        pagination.page === 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-green-700 text-white hover:bg-green-800'
                    ]">
                        Previous
                    </button>
                    <span class="px-4 py-2 text-gray-700 font-marketing">
                        Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                    </span>
                    <button @click="goToPage(pagination.page + 1)" :disabled="pagination.page >= pagination.total_pages"
                        :class="[
                            'px-4 py-2 rounded-md font-marketing transition-colors',
                            pagination.page >= pagination.total_pages
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-green-700 text-white hover:bg-green-800'
                        ]">
                        Next
                    </button>
                </div>

                <div v-if="selectedBooks.length > 0" class="border-t pt-6 mt-6">
                    <h4 class="font-header text-[1.25rem] font-bold mb-4">Slider Order ({{ selectedBooks.length }}
                        books)</h4>
                    <div class="space-y-3">
                        <div v-for="(book, index) in selectedBooks" :key="book.id"
                            class="p-4 border-2 rounded-lg border-gray-200 hover:border-gray-300 transition-all">
                            <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
                                <div class="flex-shrink-0 text-gray-400 font-header font-bold text-lg w-8">
                                    {{ index + 1 }}
                                </div>
                                <div class="flex-shrink-0">
                                    <img v-if="book.cover_image" :src="book.cover_image" :alt="book.title"
                                        class="w-16 h-20 object-cover" />
                                    <div v-else class="w-16 h-20 bg-gray-200 rounded flex items-center justify-center">
                                        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                </div>
                                <div class="flex-1 min-w-0 flex flex-col gap-2">
                                    <div>
                                        <h4 class="font-header text-[1.5rem] font-bold text-primary">{{ book.title }}
                                        </h4>
                                        <p class="text-sm text-gray-600 font-body text-body leading-[1.75]">{{
                                            book.author }}</p>
                                    </div>
                                    <div class="flex flex-wrap gap-2 justify-start sm:justify-end sm:self-end">
                                        <button @click.stop="moveUp(index)" :disabled="index === 0" :class="[
                                            'p-2 rounded transition-colors',
                                            index === 0
                                                ? 'text-gray-300 cursor-not-allowed'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        ]">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M5 15l7-7 7 7" />
                                            </svg>
                                        </button>
                                        <button @click.stop="moveDown(index)"
                                            :disabled="index === selectedBooks.length - 1" :class="[
                                                'p-2 rounded transition-colors',
                                                index === selectedBooks.length - 1
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                            ]">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        <button @click.stop="removeFromSlider(index)"
                                            class="p-2 rounded text-red-600 hover:bg-red-50 transition-colors">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="selectedBooks.length > 0" class="flex justify-end gap-3 mt-6 pt-6 border-t">
                <button @click="saveSliderConfig" :disabled="saving"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-body disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ saving ? 'Saving...' : 'Save Slider Configuration' }}
                </button>
            </div>
        </div>

        <div class="border border-gray-200 rounded-lg p-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-2">Empty State Messages</h3>
            <p class="text-sm text-gray-600 font-body text-body leading-[1.75] mb-4">Edit the messages displayed when no
                books are found for each section or category.</p>

            <div class="space-y-4">
                <div v-for="(message, key) in emptyMessages" :key="key"
                    class="border-b border-gray-200 pb-4 last:border-b-0">
                    <label class="block text-sm font-body text-body leading-[1.75] font-bold text-gray-700 mb-2">
                        {{ getMessageLabel(key) }}
                    </label>
                    <input v-model="emptyMessages[key]" type="text"
                        class="w-full px-4 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent font-body text-body leading-[1.75] text-primary"
                        :placeholder="getMessagePlaceholder(key)" />
                    <p class="text-xs text-gray-500 mt-1 font-body text-body leading-[1.75]">
                        <span v-if="key === 'search'">Use {{ query }} as a placeholder for the search term</span>
                    </p>
                </div>
            </div>

            <div class="flex justify-end gap-3 mt-6 pt-6 border-t">
                <button @click="saveEmptyMessages" :disabled="savingMessages"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-body disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ savingMessages ? 'Saving...' : 'Save Messages' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'

const paginatedBooks = ref([])
const selectedBooks = ref([])
const saving = ref(false)
const savingMessages = ref(false)
const { loading, setLoading } = useLoadingState(false)
const searchQuery = ref('')
const emptyMessages = ref({})
const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
})

let searchTimeout = null

const isBookSelected = (bookId) => {
    return selectedBooks.value.some(book => book.id === bookId)
}

const toggleBookSelection = (bookId) => {
    const index = selectedBooks.value.findIndex(book => book.id === bookId)
    if (index === -1) {
        const book = paginatedBooks.value.find(b => b.id === bookId)
        if (book) {
            selectedBooks.value.push(book)
        }
    } else {
        selectedBooks.value.splice(index, 1)
    }
}

const removeFromSlider = (index) => {
    selectedBooks.value.splice(index, 1)
}

const moveUp = (index) => {
    if (index > 0) {
        const book = selectedBooks.value[index]
        selectedBooks.value.splice(index, 1)
        selectedBooks.value.splice(index - 1, 0, book)
    }
}

const moveDown = (index) => {
    if (index < selectedBooks.value.length - 1) {
        const book = selectedBooks.value[index]
        selectedBooks.value.splice(index, 1)
        selectedBooks.value.splice(index + 1, 0, book)
    }
}

const handleSearch = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout)
    }

    searchTimeout = setTimeout(() => {
        pagination.value.page = 1
        loadBooks()
    }, 300)
}

const clearSearch = () => {
    searchQuery.value = ''
    pagination.value.page = 1
    loadBooks()
}

const goToPage = (page) => {
    if (page >= 1 && page <= pagination.value.total_pages) {
        pagination.value.page = page
        loadBooks()
    }
}

const loadBooks = async () => {
    setLoading(true)
    try {
        const params = {
            page: pagination.value.page,
            limit: pagination.value.limit
        }

        if (searchQuery.value.trim()) {
            params.search = searchQuery.value.trim()
        }

        const response = await adminService.getBooks(params)

        if (response.pagination) {
            paginatedBooks.value = response.books || []
            pagination.value = {
                page: response.pagination.page,
                limit: response.pagination.limit,
                total: response.pagination.total,
                total_pages: response.pagination.total_pages
            }
        } else {
            paginatedBooks.value = Array.isArray(response) ? response : []
            pagination.value = {
                page: 1,
                limit: paginatedBooks.value.length,
                total: paginatedBooks.value.length,
                total_pages: 1
            }
        }
    } catch (error) {
        console.error('Failed to load books:', error)
        paginatedBooks.value = []
    } finally {
        setLoading(false)
    }
}

const loadSliderConfig = async () => {
    try {
        const config = await adminService.getSliderConfig()
        if (config && config.book_ids && config.book_ids.length > 0) {
            const allBooksMap = new Map()

            let currentPage = 1
            let hasMore = true

            while (hasMore) {
                const params = {
                    page: currentPage,
                    limit: 100
                }

                const response = await adminService.getBooks(params)
                const books = response.pagination ? response.books : response

                books.forEach(book => {
                    allBooksMap.set(book.id, book)
                })

                if (response.pagination) {
                    hasMore = currentPage < response.pagination.total_pages
                    currentPage++
                } else {
                    hasMore = false
                }
            }

            const orderedBooks = config.book_ids
                .map(id => allBooksMap.get(id))
                .filter(book => book !== undefined)
            selectedBooks.value = orderedBooks
        }
    } catch (error) {
        console.error('Failed to load slider config:', error)
    }
}

const saveSliderConfig = async () => {
    saving.value = true
    try {
        const bookIds = selectedBooks.value.map(book => book.id)
        await adminService.updateSliderConfig({ book_ids: bookIds })
        alert('Slider configuration saved successfully!')
    } catch (error) {
        console.error('Failed to save slider config:', error)
        alert('Failed to save slider configuration. Please try again.')
    } finally {
        saving.value = false
    }
}

const getMessageLabel = (key) => {
    const labels = {
        'search': 'Search Results',
        'bestsellers': 'Bestsellers',
        'new-releases': 'New Releases',
        'trending': 'Trending Now',
        'recommended-reads': 'Recommended Reads',
        'coming-soon': 'Coming Soon',
        'cooking': 'Category Cooking',
        'childrens-books': 'Category Children\'s Books',
        'education': 'Category Education',
        'fiction': 'Category Fiction',
        'mystery': 'Category Mystery',
        'thriller': 'Category Thriller',
        'action': 'Category Action',
        'nonfiction': 'Category Nonfiction',
        'romance': 'Category Romance',
        'fantasy': 'Category Fantasy',
        'author': 'Author Page',
        'slider': 'Slider',
        'all': 'All Books'
    }
    return labels[key] || key
}

const getMessagePlaceholder = (key) => {
    const placeholders = {
        'search': 'No books found for "{{query}}".',
        'bestsellers': 'No books found for Bestsellers.',
        'new-releases': 'No books found for New Releases.',
        'trending': 'No books found for Trending Now.',
        'recommended-reads': 'No books found for Recommended Reads.',
        'coming-soon': 'No books found for Coming Soon.',
        'cooking': 'No books found for Category Cooking.',
        'childrens-books': 'No books found for Category Children\'s Books.',
        'education': 'No books found for Category Education.',
        'fiction': 'No books found for Category Fiction.',
        'mystery': 'No books found for Category Mystery.',
        'thriller': 'No books found for Category Thriller.',
        'action': 'No books found for Category Action.',
        'nonfiction': 'No books found for Category Nonfiction.',
        'romance': 'No books found for Category Romance.',
        'fantasy': 'No books found for Category Fantasy.',
        'author': 'No books found for this author.',
        'slider': 'No books found for the slider.',
        'all': 'No books found.'
    }
    return placeholders[key] || ''
}

const loadEmptyMessages = async () => {
    try {
        const response = await adminService.getBookEmptyMessages()
        if (response && response.messages) {
            emptyMessages.value = { ...response.messages }
        }
    } catch (error) {
        console.error('Failed to load empty messages:', error)
    }
}

const saveEmptyMessages = async () => {
    savingMessages.value = true
    try {
        await adminService.updateBookEmptyMessages(emptyMessages.value)
        alert('Empty messages saved successfully!')
    } catch (error) {
        console.error('Failed to save empty messages:', error)
        alert('Failed to save empty messages. Please try again.')
    } finally {
        savingMessages.value = false
    }
}

onMounted(async () => {
    await loadBooks()
    await loadSliderConfig()
    await loadEmptyMessages()
})
</script>
